import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAccessToken } from '@features/user';
import {
  AccessTime,
  ArrowDropDown,
  CheckCircle,
  Error as ErrorIcon,
  ExpandLess,
  ExpandMore,
  Mic,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import APIService from '@services/APIService';

interface TestStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'success' | 'error';
  duration?: number;
  error?: string;
  details?: Record<string, unknown>;
}

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

const StepContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

interface TestRecordingWidgetProps {
  onTestComplete?: (results: { success: boolean; steps: TestStep[]; timestamp: string }) => void;
}

export const TestRecordingWidget: React.FC<TestRecordingWidgetProps> = ({ onTestComplete }) => {
  const { getAccessToken } = useAccessToken();
  const [isRecording, setIsRecording] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testSteps, setTestSteps] = useState<TestStep[]>([]);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const recordedChunks = useRef<Blob[]>([]);
  const recordedBlob = useRef<Blob | undefined>(undefined);
  const audioContext = useRef<AudioContext | null>(null);
  const microphone = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const animationFrame = useRef<number | null>(null);
  const stream = useRef<MediaStream | null>(null);
  // Removed unused state variables - using refs for real-time tracking instead
  const testStepsRef = useRef<TestStep[]>([]); // Keep a ref copy for immediate access
  const maxAudioLevelRef = useRef<number>(0); // Real-time tracking of max audio level
  const audioDetectedRef = useRef<boolean>(false); // Real-time tracking of audio detection
  const isAnalyzingRef = useRef<boolean>(false); // Real-time tracking of analysis state

  // Microphone selection state
  const [availableMicrophones, setAvailableMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');
  const [microphonePermissionGranted, setMicrophonePermissionGranted] = useState<boolean | null>(
    null,
  );

  // Initialize test steps
  const initializeTestSteps = (): TestStep[] => [
    { id: 'mic-permission', label: 'Check Microphone Permission', status: 'pending' },
    { id: 'mic-test', label: 'Test Microphone Audio', status: 'pending' },
    { id: 'start-recording', label: 'Start Recording (1 second)', status: 'pending' },
    { id: 'stop-recording', label: 'Stop Recording', status: 'pending' },
    { id: 'get-upload-url', label: 'Get Test Upload URL', status: 'pending' },
    { id: 's3-upload', label: 'Upload to S3', status: 'pending' },
  ];

  useEffect(() => {
    // Get available microphones on component mount
    getAvailableMicrophones();

    // Cleanup on unmount
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
      if (audioContext.current && audioContext.current.state !== 'closed') {
        audioContext.current.close();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getAvailableMicrophones = async () => {
    try {
      // First check if we have permission
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === 'audioinput');
      setAvailableMicrophones(microphones);

      // Set default microphone if not selected
      if (!selectedMicrophone && microphones.length > 0) {
        // Try to get the stored microphone from localStorage
        const storedMicId = localStorage.getItem('selectedMicrophoneId');
        const storedMic = microphones.find(mic => mic.deviceId === storedMicId);
        if (storedMic) {
          setSelectedMicrophone(storedMic.deviceId);
        } else {
          setSelectedMicrophone(microphones[0].deviceId);
        }
      }
    } catch (error) {
      console.error('Error getting microphones:', error);
    }
  };

  const handleMicrophoneChange = (event: SelectChangeEvent) => {
    const micId = event.target.value;
    setSelectedMicrophone(micId);
    localStorage.setItem('selectedMicrophoneId', micId);
  };

  const updateStep = (stepId: string, updates: Partial<TestStep>, startTime?: number) => {
    const updater = (prev: TestStep[]) =>
      prev.map(step => {
        if (step.id === stepId) {
          const duration = startTime ? Date.now() - startTime : step.duration;
          return { ...step, ...updates, duration };
        }
        return step;
      });

    // Update both state and ref
    setTestSteps(updater);
    testStepsRef.current = updater(testStepsRef.current);
  };

  const analyzeAudioLevel = useCallback(() => {
    // Check if we should continue analyzing (prevents race condition)
    if (!analyser.current || !isAnalyzingRef.current) {
      return;
    }

    const dataArray = new Uint8Array(analyser.current.frequencyBinCount);
    analyser.current.getByteFrequencyData(dataArray);

    // Calculate average volume
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    setAudioLevel(average);

    // Track max audio level and detection (using both state and refs)
    if (average > maxAudioLevelRef.current) {
      maxAudioLevelRef.current = average;
    }

    if (average > 2 && !audioDetectedRef.current) {
      audioDetectedRef.current = true;
    }

    // Only continue if we're still analyzing
    if (isAnalyzingRef.current) {
      animationFrame.current = requestAnimationFrame(analyzeAudioLevel);
    }
  }, []);

  const runTest = async () => {
    setIsTesting(true);
    const initialSteps = initializeTestSteps();
    setTestSteps(initialSteps);
    testStepsRef.current = initialSteps; // Initialize the ref
    recordedBlob.current = undefined;
    recordedChunks.current = [];
    setAudioLevel(0);
    maxAudioLevelRef.current = 0;
    audioDetectedRef.current = false;
    isAnalyzingRef.current = false;

    try {
      // Step 1: Check microphone permission
      let startTime = Date.now();
      updateStep('mic-permission', { status: 'running' });

      // Use selected microphone if available
      const constraints: MediaStreamConstraints = {
        audio: selectedMicrophone ? { deviceId: { exact: selectedMicrophone } } : true,
        video: false,
      };

      stream.current = await navigator.mediaDevices.getUserMedia(constraints);
      setMicrophonePermissionGranted(true);

      // Refresh microphone list after permission granted
      await getAvailableMicrophones();

      updateStep(
        'mic-permission',
        {
          status: 'success',
          details: {
            tracks: stream.current.getAudioTracks().length,
            trackSettings: stream.current.getAudioTracks()[0]?.getSettings(),
            selectedMicrophone: selectedMicrophone || 'default',
          },
        },
        startTime,
      );

      // Step 2: Test microphone audio
      startTime = Date.now();
      updateStep('mic-test', { status: 'running' });

      // Set up audio analysis
      audioContext.current = new AudioContext();

      // Check if the audio context needs to be resumed (Chrome autoplay policy)
      if (audioContext.current.state === 'suspended') {
        await audioContext.current.resume();
      }

      microphone.current = audioContext.current.createMediaStreamSource(stream.current);

      analyser.current = audioContext.current.createAnalyser();
      analyser.current.fftSize = 256;
      analyser.current.smoothingTimeConstant = 0.8;

      microphone.current.connect(analyser.current);

      // Start analyzing audio
      setIsRecording(true); // For UI state
      isAnalyzingRef.current = true; // Start analyzing immediately
      analyzeAudioLevel();

      // Wait for 2 seconds to collect audio data
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Stop audio analysis
      setIsRecording(false); // Stop UI state
      isAnalyzingRef.current = false; // Stop analyzing
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
        animationFrame.current = null;
      }

      const hasAudio = audioDetectedRef.current || maxAudioLevelRef.current > 2; // Use refs for real-time values
      updateStep(
        'mic-test',
        {
          status: hasAudio ? 'success' : 'error',
          error: hasAudio
            ? undefined
            : 'No audio detected from microphone. Try speaking louder or selecting a different microphone.',
          details: {
            maxAudioLevel: maxAudioLevelRef.current.toFixed(2),
            hasAudio,
            threshold: 2,
            selectedMicrophone: selectedMicrophone || 'default',
          },
        },
        startTime,
      );

      if (!hasAudio) {
        throw new Error('No audio detected from microphone');
      }

      // Step 3: Start recording
      startTime = Date.now();
      updateStep('start-recording', { status: 'running' });

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      mediaRecorder.current = new MediaRecorder(stream.current, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      // Create a promise to wait for the recording to complete
      const recordingPromise = new Promise<void>(resolve => {
        // Set up event handlers
        if (mediaRecorder.current) {
          mediaRecorder.current.ondataavailable = event => {
            if (event.data.size > 0) {
              recordedChunks.current.push(event.data);
            }
          };

          mediaRecorder.current.onstop = () => {
            recordedBlob.current = new Blob(recordedChunks.current, { type: mimeType });
            resolve();
          };
        }
      });

      // Start recording
      mediaRecorder.current.start();
      setIsRecording(true);

      updateStep(
        'start-recording',
        {
          status: 'success',
          details: {
            mimeType,
            audioBitsPerSecond: 128000,
          },
        },
        startTime,
      );

      // Wait for 1 second
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 4: Stop recording
      startTime = Date.now();
      updateStep('stop-recording', { status: 'running' });

      mediaRecorder.current.stop();
      setIsRecording(false);

      // Wait for the recording to be processed
      await recordingPromise;

      // TypeScript needs help understanding that recordedBlob.current is set in the promise

      if (!recordedBlob.current) {
        throw new Error('No recording blob available');
      }
      const recordingBlob = recordedBlob.current as Blob;

      if (recordingBlob.size === 0) {
        throw new Error('Recording blob is empty');
      }

      updateStep(
        'stop-recording',
        {
          status: 'success',
          details: {
            blobSize: recordingBlob.size,
            blobType: recordingBlob.type,
          },
        },
        startTime,
      );

      // Step 5: Get test upload URL
      startTime = Date.now();
      updateStep('get-upload-url', { status: 'running' });

      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/v2/provider/transcriptions/test-upload-url',
        accessToken,
        body: {
          content_type: recordingBlob.type || 'audio/mpeg',
        },
      });

      if (!response.ok || !response.value) {
        throw new Error('Failed to get test upload URL');
      }

      interface TestUploadResponse {
        upload_url: string;
        s3_object_id: string;
      }

      const uploadData = response.value as TestUploadResponse;
      updateStep(
        'get-upload-url',
        {
          status: 'success',
          details: {
            s3_object_id: uploadData.s3_object_id,
            urlLength: uploadData.upload_url.length,
          },
        },
        startTime,
      );

      // Step 6: Upload to S3
      startTime = Date.now();
      updateStep('s3-upload', { status: 'running' });

      const uploadResponse = await fetch(uploadData.upload_url, {
        method: 'PUT',
        body: recordingBlob,
        headers: {
          'Content-Type': recordingBlob.type || 'audio/mpeg',
        },
      });

      // Check if response is in 2xx range
      if (uploadResponse.status < 200 || uploadResponse.status >= 300) {
        const errorMessage = `S3 upload failed with status ${uploadResponse.status}`;
        updateStep(
          's3-upload',
          {
            status: 'error',
            error: errorMessage,
            details: {
              statusCode: uploadResponse.status,
              statusText: uploadResponse.statusText,
              bytesAttempted: recordingBlob.size,
            },
          },
          startTime,
        );
        throw new Error(errorMessage);
      }

      updateStep(
        's3-upload',
        {
          status: 'success',
          details: {
            statusCode: uploadResponse.status,
            bytesUploaded: recordingBlob.size,
          },
        },
        startTime,
      );
    } catch (error) {
      console.error('Test failed:', error);

      // Update the current running step as failed
      const runningStep = testStepsRef.current.find(step => step.status === 'running');
      if (runningStep) {
        let errorMessage = error instanceof Error ? error.message : 'Unknown error';

        // Provide more specific error messages for common issues
        if (error instanceof Error) {
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            errorMessage =
              'Microphone permission denied. Please allow microphone access and try again.';
          } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
            errorMessage = 'No microphone found. Please connect a microphone and try again.';
          } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
            errorMessage =
              'Microphone is in use by another application. Please close other apps using the microphone.';
          }
        }

        updateStep(runningStep.id, {
          status: 'error',
          error: errorMessage,
        });
      }
    } finally {
      // Cleanup
      setIsTesting(false);
      setIsRecording(false);
      setAudioLevel(0);
      isAnalyzingRef.current = false;

      // Cancel any pending animation frame
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
        animationFrame.current = null;
      }

      // Stop all tracks
      if (stream.current) {
        stream.current.getTracks().forEach(track => {
          track.stop();
        });
        stream.current = null;
      }

      // Disconnect audio nodes before closing context
      if (microphone.current) {
        microphone.current.disconnect();
        microphone.current = null;
      }
      if (analyser.current) {
        analyser.current.disconnect();
        analyser.current = null;
      }

      // Close audio context
      if (audioContext.current && audioContext.current.state !== 'closed') {
        await audioContext.current.close();
        audioContext.current = null;
      }

      // Call the completion callback with the test results
      if (onTestComplete) {
        const testResults = {
          success: testStepsRef.current.every(step => step.status === 'success'),
          steps: testStepsRef.current,
          timestamp: new Date().toISOString(),
        };
        onTestComplete(testResults);
      }
    }
  };

  const getStepIcon = (status: TestStep['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'running':
        return <CircularProgress size={20} />;
      default:
        return <AccessTime color="disabled" />;
    }
  };

  const getStatusChip = (step: TestStep) => {
    if (step.status === 'success' && step.duration) {
      return <Chip label={`${step.duration}ms`} size="small" color="success" variant="outlined" />;
    }
    if (step.status === 'error') {
      return <Chip label="Failed" size="small" color="error" />;
    }
    if (step.status === 'running') {
      return <Chip label="Running..." size="small" color="primary" />;
    }
    return null;
  };

  const toggleStepExpansion = (stepId: string) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  return (
    <Card>
      <CardContent>
        <SectionTitle variant="h6">Recording Test</SectionTitle>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Test the complete recording flow including microphone access, audio capture, and S3
            upload.
          </Typography>
        </Box>

        {/* Microphone Selection */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="microphone-select-label">Microphone</InputLabel>
            <Select
              labelId="microphone-select-label"
              id="microphone-select"
              value={selectedMicrophone}
              label="Microphone"
              onChange={handleMicrophoneChange}
              disabled={isTesting || isRecording}
              IconComponent={ArrowDropDown}
            >
              {availableMicrophones.length === 0 ? (
                <MenuItem value="">
                  <em>No microphones detected</em>
                </MenuItem>
              ) : (
                availableMicrophones.map(mic => (
                  <MenuItem key={mic.deviceId} value={mic.deviceId}>
                    {mic.label || `Microphone ${mic.deviceId.substring(0, 8)}...`}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {!microphonePermissionGranted && availableMicrophones.length === 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Grant microphone permission to see available devices
            </Typography>
          )}
        </Box>

        {/* Audio Level Indicator */}
        {isRecording && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Audio Level
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (audioLevel / 128) * 100)}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Test Steps */}
        {testSteps.length > 0 && (
          <List sx={{ mb: 2 }}>
            {testSteps.map(step => (
              <StepContainer key={step.id}>
                <ListItem
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    mb: 0.5,
                    cursor: step.details ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (step.details) {
                      toggleStepExpansion(step.id);
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{getStepIcon(step.status)}</ListItemIcon>
                  <ListItemText
                    primary={step.label}
                    secondary={step.error}
                   slotProps={{ secondary: {
                      color: 'error',
                    } }}/>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusChip(step)}
                    {step.details && (expandedStep === step.id ? <ExpandLess /> : <ExpandMore />)}
                  </Box>
                </ListItem>

                {step.details && (
                  <Collapse in={expandedStep === step.id}>
                    <Box sx={{ pl: 7, pr: 2, pb: 1 }}>
                      <Typography
                        variant="caption"
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {JSON.stringify(step.details, null, 2)}
                      </Typography>
                    </Box>
                  </Collapse>
                )}
              </StepContainer>
            ))}
          </List>
        )}

        {/* Test Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={runTest}
          disabled={isTesting}
          startIcon={isTesting ? <CircularProgress size={20} /> : <Mic />}
          fullWidth
        >
          {isTesting ? 'Running Test...' : 'Start Recording Test'}
        </Button>

        {/* Summary */}
        {testSteps.length > 0 && !isTesting && (
          <Box sx={{ mt: 2 }}>
            {testSteps.every(step => step.status === 'success') ? (
              <Alert severity="success">
                All tests passed! Recording functionality is working correctly.
              </Alert>
            ) : testSteps.some(step => step.status === 'error') ? (
              <Alert severity="error">
                Some tests failed. Please check the error messages above.
              </Alert>
            ) : null}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
